// Authentication service for Google Workspace integration

import { LoginProvider } from '@repo/types';
import { api } from '@xtnd/web/shared/api';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  isAdmin: boolean;
  tenantId?: string;
  tenantAlias?: string;
  tenantName?: string;
}

export interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface SocialLoginUrlResponse {
  loginUrl: string;
  provider: string;
  tenantId: string;
}

export interface SocialLoginParams {
  provider: LoginProvider;
  tenantId?: string;
}

export interface OAuthCallbackParams {
  code: string;
  state: string;
}

export interface OAuthCallbackResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiredAt: number;
  user: {
    id: string;
    email: string;
    name: string;
    picture?: string;
  };
}

class AuthApi {
  private baseResource = '/auth';
  // Get the correct redirect URI for web app
  private getRedirectUri(): string {
    return `${window.location.origin}${this.baseResource}/callback`;
  }


  async getSocialLoginUrl({ provider, tenantId = 'auto' }: SocialLoginParams): Promise<SocialLoginUrlResponse> {
    const redirectUri = this.getRedirectUri();
    const params = new URLSearchParams({
      provider,
      tenantId,
      redirectUri
    });

    if (provider === 'Microsoft') {
      // For Microsoft, construct the backend URL and return it directly
      // The backend will handle the redirect to Microsoft without CORS issues
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const loginUrl = `${baseUrl}${this.baseResource}/microsoft/login`;

      return {
        loginUrl,
        provider: 'Microsoft',
        tenantId: tenantId
      };
    } else {
      return api.get<SocialLoginUrlResponse>(`${this.baseResource}/login-url?${params.toString()}`);
    }
  }

  // Handle OAuth callback
  async handleOAuthCallback({ code, state }: OAuthCallbackParams): Promise<OAuthCallbackResponse> {
    try {
      const response = await api.get<OAuthCallbackResponse>(`${this.baseResource}/callback?code=${code}&state=${state}`);

      // Store tokens
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('idToken', response.idToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('expiredAt', `${response.expiredAt}`);

      const userRes = await api.get<AuthUser>(`${this.baseResource}/me`);

      localStorage.setItem('user', JSON.stringify(userRes));

      return response;
    } catch (error: any) {
      // Check if this is an unauthorized domain error
      if (error?.response?.status === 401 ||
        (error?.message && error.message.includes('Access denied for domain'))) {

        // Clear any stored tokens
        this.clearAuthData();

        // Redirect to XTND.legal with error message
        const errorMessage = encodeURIComponent(
          error?.message || 'Your domain is not authorized to access this application. Please contact support.'
        );
        window.location.href = `https://xtnd.legal/?error=unauthorized_domain&message=${errorMessage}`;

        // Throw error to prevent further processing
        throw new Error('Unauthorized domain - redirecting to xtnd.legal');
      }

      // Re-throw other errors
      throw error;
    }
  }

  // Handle Microsoft OAuth callback (token already stored, just get user info)
  async handleMicrosoftCallback(): Promise<{ success: boolean }> {
    try {
      // Token should already be stored by the AuthCallback component
      // Just call /auth/me to get enriched user info (tenant alias, name, etc.)
      const userRes = await api.get<AuthUser>(`${this.baseResource}/me`);
      localStorage.setItem('user', JSON.stringify(userRes));

      return { success: true };
    } catch (error: any) {
      // Clear any stored tokens on error
      this.clearAuthData();
      throw error;
    }
  }

  // Clear authentication data
  private clearAuthData(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiredAt');
    localStorage.removeItem('user');
  }

  // Get current user from localStorage
  getCurrentUser(): AuthUser | null {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return null;

      const userData = JSON.parse(userStr);
      return {
        id: userData.id,
        email: userData.email,
        name: userData.name,
        picture: userData.picture,
        isAdmin: userData.isAdmin || false,
        tenantId: userData.tenantId,
        tenantAlias: userData.tenantAlias,
        tenantName: userData.tenantName,
      };
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      // Clear tokens and user data
      this.clearAuthData();
    } catch (error) {
      console.error('Sign out failed:', error);
      throw new Error('Failed to sign out');
    }
  }

  isAuthenticated(): boolean {
    // Check if JWT authentication is disabled (for local development)
    if ((import.meta as any).env?.VITE_DISABLE_JWT_AUTH === 'true') {
      return true;
    }

    const accessToken = localStorage.getItem('accessToken');
    const user = localStorage.getItem('user');
    return !!(accessToken && user);
  }
}

// Export singleton instance
export const authApi = new AuthApi();
